import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MessageSquare, MapPin, X, Loader2, ShieldAlert, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HOLD_MS = 1500;

/**
 * SOSModal — confirm-to-trigger emergency alert.
 *
 * Flow:
 *   1. Hold the red button for 1.5s.
 *   2. Browser captures GPS, POSTs /api/sos.
 *   3. Modal shows: emergency numbers (112/100/1091...), saved contacts with
 *      tel:/sms:/wa.me deep links, and a copyable live-location share URL.
 *   4. Heartbeat updates location every 20s for 15 minutes (or until the
 *      user taps "End share").
 */
export default function SOSModal({ open, onClose, isAuthenticated }) {
  const [phase, setPhase] = useState("idle"); // idle | holding | locating | active | error
  const [progress, setProgress] = useState(0);
  const [sos, setSos] = useState(null);
  const [copied, setCopied] = useState(false);
  const holdTimer = useRef(null);
  const progressTimer = useRef(null);
  const heartbeatTimer = useRef(null);
  const watchIdRef = useRef(null);

  useEffect(() => {
    if (!open) {
      cleanup();
      setPhase("idle");
      setProgress(0);
      setSos(null);
      setCopied(false);
    }
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const cleanup = () => {
    if (holdTimer.current) clearTimeout(holdTimer.current);
    if (progressTimer.current) clearInterval(progressTimer.current);
    if (heartbeatTimer.current) clearInterval(heartbeatTimer.current);
    if (watchIdRef.current && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }
    holdTimer.current = null;
    progressTimer.current = null;
    heartbeatTimer.current = null;
    watchIdRef.current = null;
  };

  const startHold = () => {
    if (phase !== "idle") return;
    if (!isAuthenticated) {
      toast.error("Please login to use SOS");
      return;
    }
    setPhase("holding");
    setProgress(0);
    const startedAt = Date.now();
    progressTimer.current = setInterval(() => {
      setProgress(Math.min(100, ((Date.now() - startedAt) / HOLD_MS) * 100));
    }, 50);
    holdTimer.current = setTimeout(triggerSOS, HOLD_MS);
  };

  const cancelHold = () => {
    if (phase !== "holding") return;
    if (holdTimer.current) clearTimeout(holdTimer.current);
    if (progressTimer.current) clearInterval(progressTimer.current);
    setPhase("idle");
    setProgress(0);
  };

  const triggerSOS = async () => {
    if (progressTimer.current) clearInterval(progressTimer.current);
    setProgress(100);
    setPhase("locating");

    if (!navigator.geolocation) {
      toast.error("Geolocation not supported on this device");
      setPhase("error");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const token = localStorage.getItem("token");
          const { latitude, longitude, accuracy } = pos.coords;
          const res = await axios.post(
            `${API}/sos`,
            { latitude, longitude, accuracy },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setSos(res.data);
          setPhase("active");
          toast.success("SOS active — share link ready");
          startHeartbeat(res.data.share_id);
        } catch (e) {
          console.error(e);
          toast.error(e?.response?.data?.detail || "Failed to trigger SOS");
          setPhase("error");
        }
      },
      (err) => {
        console.error(err);
        toast.error("Could not get your location. Allow GPS and try again.");
        setPhase("error");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const startHeartbeat = (shareId) => {
    if (!navigator.geolocation) return;
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        // Throttle in heartbeatTimer below
        watchIdRef.current = watchIdRef.current; // keep ref alive
        window.__lastGeo = pos.coords;
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 5000 }
    );
    heartbeatTimer.current = setInterval(async () => {
      const c = window.__lastGeo;
      if (!c) return;
      try {
        const token = localStorage.getItem("token");
        await axios.post(
          `${API}/sos/${shareId}/heartbeat`,
          { latitude: c.latitude, longitude: c.longitude, accuracy: c.accuracy },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (e) { /* ignore transient */ }
    }, 20000);
  };

  const endShare = async () => {
    if (!sos) return;
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/sos/${sos.share_id}/end`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("SOS share ended");
    } catch (e) { /* ignore */ }
    cleanup();
    onClose();
  };

  const copyShare = async () => {
    if (!sos?.share_url) return;
    try {
      await navigator.clipboard.writeText(sos.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[1000] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center"
        onClick={() => phase !== "active" && onClose()}
        data-testid="sos-modal"
      >
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 50, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl shadow-2xl max-h-[90vh] overflow-y-auto"
        >
          <div className="flex items-center justify-between p-4 border-b border-slate-100 sticky top-0 bg-white z-10">
            <div className="flex items-center gap-2">
              <ShieldAlert className="text-red-600" size={22} />
              <h2 className="font-bold text-lg">Emergency SOS</h2>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-full" data-testid="sos-close-btn">
              <X size={20} />
            </button>
          </div>

          {/* IDLE / HOLDING — confirm trigger */}
          {(phase === "idle" || phase === "holding") && (
            <div className="p-6 flex flex-col items-center text-center gap-5">
              <p className="text-slate-600 text-sm">
                Hold the button for <b>1.5 seconds</b> to start an SOS. We&apos;ll capture
                your live location and prepare quick-call links to your contacts and
                Indian emergency numbers.
              </p>
              <button
                onMouseDown={startHold}
                onMouseUp={cancelHold}
                onMouseLeave={cancelHold}
                onTouchStart={startHold}
                onTouchEnd={cancelHold}
                className="relative w-40 h-40 rounded-full bg-red-600 text-white font-bold shadow-lg flex items-center justify-center select-none active:scale-95 transition-transform"
                data-testid="sos-hold-btn"
              >
                <span className="z-10 flex flex-col items-center gap-1">
                  <ShieldAlert size={36} />
                  <span>{phase === "holding" ? "Hold..." : "HOLD TO SEND"}</span>
                </span>
                <span
                  className="absolute inset-0 rounded-full bg-red-800 origin-bottom"
                  style={{ transform: `scaleY(${progress / 100})`, transition: "transform 50ms linear" }}
                />
              </button>
              <p className="text-xs text-slate-400">Release before 1.5s to cancel.</p>
            </div>
          )}

          {phase === "locating" && (
            <div className="p-8 flex flex-col items-center gap-3">
              <Loader2 className="animate-spin text-red-600" size={32} />
              <p className="text-sm text-slate-600">Capturing your location…</p>
            </div>
          )}

          {phase === "error" && (
            <div className="p-6 text-center">
              <p className="text-red-600 font-medium mb-3">Something went wrong.</p>
              <Button onClick={() => setPhase("idle")} variant="outline">Try again</Button>
            </div>
          )}

          {/* ACTIVE — quick-dial */}
          {phase === "active" && sos && (
            <div className="p-4 space-y-5" data-testid="sos-active-panel">
              {/* Live share */}
              <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                <p className="text-xs font-semibold text-red-700 mb-1">LIVE LOCATION SHARING</p>
                <p className="text-xs text-slate-600 break-all mb-2">{sos.share_url}</p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={copyShare}
                    variant="outline"
                    className="flex-1"
                    data-testid="sos-copy-share-btn"
                  >
                    {copied ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
                    {copied ? "Copied" : "Copy link"}
                  </Button>
                  <a
                    href={sos.google_maps_url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-1 text-sm font-medium border border-slate-200 rounded-md px-3 py-1.5 hover:bg-slate-50"
                    data-testid="sos-open-maps"
                  >
                    <MapPin size={14} /> Open in Maps
                  </a>
                </div>
              </div>

              {/* Emergency Numbers */}
              <div>
                <p className="text-xs font-semibold text-slate-500 mb-2">EMERGENCY NUMBERS (INDIA)</p>
                <div className="grid grid-cols-2 gap-2">
                  {sos.emergency_numbers.map((n) => (
                    <a
                      key={n.number}
                      href={n.tel_url}
                      className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white rounded-lg px-3 py-2 text-sm font-medium"
                      data-testid={`sos-emergency-${n.number}`}
                    >
                      <Phone size={14} />
                      <span className="flex-1 truncate">{n.label}</span>
                      <b>{n.number}</b>
                    </a>
                  ))}
                </div>
              </div>

              {/* Saved Contacts */}
              <div>
                <p className="text-xs font-semibold text-slate-500 mb-2">
                  YOUR EMERGENCY CONTACTS ({sos.contacts.length})
                </p>
                {sos.contacts.length === 0 ? (
                  <p className="text-sm text-slate-500 italic">
                    No saved contacts yet. Add some from your Profile to get one-tap calls.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {sos.contacts.map((c, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 bg-slate-50 rounded-lg p-2"
                        data-testid={`sos-contact-${i}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{c.name}</p>
                          <p className="text-xs text-slate-500 truncate">{c.phone}</p>
                        </div>
                        <a href={c.tel_url} className="bg-red-600 text-white rounded-md p-2" data-testid={`sos-contact-call-${i}`} title="Call">
                          <Phone size={16} />
                        </a>
                        <a href={c.sms_url} className="bg-sky-600 text-white rounded-md p-2" title="SMS">
                          <MessageSquare size={16} />
                        </a>
                        <a href={c.whatsapp_url} target="_blank" rel="noreferrer" className="bg-green-600 text-white rounded-md p-2" title="WhatsApp">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.464 3.488"/></svg>
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button
                onClick={endShare}
                variant="outline"
                className="w-full"
                data-testid="sos-end-share-btn"
              >
                End SOS share
              </Button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
